// Side-effect import: the class below is referenced only as a type, and
// without this the bundler drops the module, the custom element never
// registers, and `hydrate` hands back an un-upgraded <std-property-list>.
import "@tento-ui/components/input/property-list";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import type { PropertyList } from "@tento-ui/components/input/property-list";

/** The `kv/v1` shape — a record's key/value pairs, rendered by the
 * property list this repo already owns.
 *
 * It used to be a hand-rolled two-column grid. `std-property-list` is the
 * same object with a label column that holds, a value column that
 * stretches, and the app's own label/value type ramp; the spool's task
 * detail already uses it for exactly this job. Reimplementing it here gave
 * the transcript a second, slightly-different key/value look for no
 * reason.
 *
 * What this component owns is DENSITY, and the transcript's own type
 * scale. A transcript row sits tighter than a detail pane, so it closes
 * the gap and the row height through the variables the list exposes.
 *
 * It does set a size, which an earlier revision was told off for, and the
 * difference is worth stating because the earlier version was genuinely
 * wrong. That one set `--label-size`, `--p-size` AND `--font-size` to
 * hand-picked pixels, which is not configuring a component but reaching
 * past its design and re-deciding the app's typography from inside a leaf.
 * This sets one thing — `--turn-text`, the family-wide token the
 * transcript declares — on the label and the value alike, so the two cells
 * of a key/value pair are the same size. That is a decision about THIS
 * grid, made in the vocabulary the rest of the turn primitives share.
 *
 * It still does not touch the value's font family.
 *
 * Honest status: nothing emits `kv/v1` yet. Across 28,127 live records not
 * one carries a record-level datatype, so this primitive has never
 * rendered outside its own storybook. */
@component({ name: "std-turn-kv-grid" })
export class TurnKvGrid extends LonaWebComponent {
  private $list: PropertyList = this.hydrate("list");

  static makeWith(props: TurnKvGrid.Props): TurnKvGrid {
    return (TurnKvGrid.make() as TurnKvGrid).bind(props);
  }

  bind(props: TurnKvGrid.Props): this {
    const items: PropertyList.Items = {};
    props.pairs.forEach(([key, value], index) => {
      // Typography.small — the same call every other property list in the
      // repo makes for a value, so the value is a ramp block and the
      // stylesheet's --font-size reaches it. A bare span inherits nothing
      // and sizes itself off whatever the host happens to set, which is
      // how this component once ended up outside the ramp entirely.
      items[`${index}-${key}`] = {
        $e: Typography.small(value),
        // Values read left-to-right against their labels. The list's
        // default is flush-right, which is right for a detail pane of
        // short facts and wrong for a transcript row whose values are
        // paths, ids and sentences of varying length.
        valueAlign: "start",
        label: key,
        order: index,
        valueLayout: "stretch",
      };
    });
    this.$list.bind(items);

    // A GRID needs a value column, and a flex row does not give you one:
    // every row's value starts wherever its own label happens to end, so
    // three pairs land their values at three different x. (Measured, before
    // this line: 429.5px, 434px, 443.6px.) The list has always exposed
    // --label-min-width for exactly this and nothing was setting it.
    //
    // The width comes from the CONTENT rather than a picked number: the
    // longest key, in the label's own `ch`. `ch` is the width of "0", which
    // in a proportional face runs a little wide of the average glyph, so the
    // column is generous rather than tight — the failure mode that keeps the
    // values aligned instead of ragged. The cap stops one pathological key
    // from eating the row.
    const widest = props.pairs.reduce((n, [key]) => Math.max(n, key.length), 0);
    this.$list.style.setProperty(
      "--label-min-width",
      `min(calc(${widest}ch + var(--label-column-pad, 12px)), 40%)`,
    );
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      /* Density only. The list's own type ramp is the app's ramp; the
         earlier version overrode --label-size/--p-size/--font-size here and
         the review was blunt about it — that is not configuring a
         component, it is reaching past its design. */
      #list {
        --plist-gap: 2px;
        /* A MINIMUM row height, not a fixed one — the list applies it as
           min-height throughout, so a value that wraps grows its row
           instead of spilling out of it.

           24px, and the number is measured rather than picked: at 22px the
           label's box stopped at the floor while the value's grew to its
           own content height of 23.83px, so the two hover rectangles were
           concentric but 1.8px different in height. A floor above BOTH
           natural heights makes them the same box, which is the last part
           of "text looks more centered but not the hover state". */
        /* No floor. Revised on review: "values need consistent block padding
           so when it's multiple lines, it's still aligned. idea should be
           that the first line of a multiline text is centered to the key."

           A row height is the wrong instrument for that, and this is the
           third round it has cost. The list writes --plist-height onto the
           VALUE ELEMENT as a min-height, so a one-line value became a 24px
           box and the cell's centring had nothing left to do — a box that
           already fills its container cannot be centred in it. Last round
           fixed that by centring the line inside the box, which is right for
           one line and wrong for two: a two-line value centres as a BLOCK, so
           its first line rides above the key it belongs to.

           There is no height that makes both cases work, because the thing
           being aligned is a LINE, not a box. So the floor goes to zero and
           the rhythm comes from padding the two cells identically instead —
           below. Equal padding, equal type size, so the first line of either
           cell starts at the same offset from the row's top however many
           lines follow it. */
        --plist-height: 0px;
        /* TOP, which is where a first line lives.

           The history here is four answers, and the first three all argued
           about how to align two BOXES. Top-aligned against a 22px floor
           pinned a one-line value to the top of a taller row. Centred put
           the boxes' midpoints within 0.2px and still read low, because the
           two cells were set at different sizes. Equal sizes plus centring
           fixed both — for one line.

           What survives all four is this: the key is one line, so aligning
           to a first line is the only rule that does not change meaning when
           a value wraps. With no floor and equal padding on both cells, top
           alignment IS first-line alignment, and it stays that way at two
           lines or ten. Centring was never the answer; it was a way of
           agreeing with a floor that should not have been there. */
        --plist-align: flex-start;
        /* The key and the value, one size, and the FINE one. Revised on
           review: "typography too big".

           Equal sizes are what makes the hover land (see the alignment
           note above), but the register was wrong: this is a block of many
           short strings, not a line a reader reads, and at the row size it
           competed with the words above it. A key/value grid is a set of
           facts about a row, which is exactly the job --caption-size
           names in the app's ramp. */
        /* One size for both cells, and it is an ALIAS, not a number.
           Revised on review: "typography too big" — an earlier revision
           set a raw pixel value here, which the ramp reads as a cap-height
           box and scales up by 1/0.72, so the values rendered far larger
           than the stylesheet said. An alias is already in the ramp's own
           unit and cannot be wrong that way.

           Both cells read the same one, which is what keeps their boxes
           congruent and the hover landing on the text. */
        --label-size: var(--turn-fine-size, var(--text-8px));
        /* Both cells, one inset. The label is a button and carries a hit
           target; a slotted component value was getting padding: 0, so the
           label's text started 4px below the value's on every row — the
           label's own padding-top, measured, and the second half of why
           this read as unaligned. Tighter than the list's own 4px 8px
           because a transcript row is denser than a detail pane; that is
           this component's one job. */
        --plist-pad: var(--kv-cell-pad-y) var(--kv-cell-pad-x);
        /* The SAME vertical pad on the value cell, written from the same
           pair. This is what "consistent block padding" buys: with no
           min-height on either cell, each one is its own content plus this
           inset, so both first lines start at the same offset from the row's
           top and the two hover rectangles are congruent for a one-line
           value — the property the previous rounds were protecting — while a
           value that wraps simply grows downward past a key that does not.

           It used to be 0px 6px, zeroed to stop the cell's padding
           double-insetting a value that already claimed the row height
           through min-height. With the floor gone there is nothing to
           double-inset, and the zero is now the thing that would break the
           alignment it was protecting. */
        --plist-value-pad: var(--kv-cell-pad-y) var(--kv-cell-pad-x);
        /* One inset, two cells. Tighter than the list's own 4px 8px because
           a transcript row is denser than a detail pane; that is this
           component's one job. */
        --kv-cell-pad-y: 3px;
        --kv-cell-pad-x: 6px;
        --label-column-pad: 12px;
        --margin-top: 0px;
        --margin-bottom: 0px;
      }
      /* The value's own element, from Typography.

         --font-size, never a literal font-size: the ramp divides the given
         number by the face's cap-height and trims the block's margins from
         the result, so a literal wins the size and leaves a trim computed
         from a different one. The margins are then zeroed because a list
         row supplies its own rhythm through --plist-gap; an earlier
         revision added the ramp's asymmetric trim back (0.125 above, 0.155
         below) to make box-centring land, which is arithmetic the row does
         not need once both cells are the same size. */
      #list p {
        --font-size: var(--turn-fine-size, var(--text-8px));
        --margin-top: 0px;
        --margin-bottom: 0px;
        overflow-wrap: anywhere;
        /* A plain block again. Centring the line inside the value was last
           round's fix for a box that no longer exists — with --plist-height
           at zero the value is exactly its own text, so there is nothing to
           centre it in, and a flex box would only re-introduce a wrapper
           between the cell's alignment and the line it is aligning. */
        min-width: 0;
      }
    `,
  ];

  /* `flat` drops the list's own card. A property list draws a surface
     because it is usually the content of a detail pane; a transcript row
     already sits on the turn's surface, so the card was a second one
     stacked on the first. It also means the pairs no longer depend on a
     theme resolving to stay legible — unthemed, the card's background
     token collapsed to black and painted over every value in the grid. */
  static $html: Option<HTMLTemplateElement> = html`
    <std-property-list id="list" flat></std-property-list>
  `;
}

export namespace TurnKvGrid {
  export type Props = {
    /** Ordered pairs, as the record carries them. */
    pairs: [string, string][];
  };
}
